import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import appConfig from "../../appConfig";
import { getToken } from "../../services/apiService";
import { environmentLabel } from "./template";

const runText = (run) => `#${run.inputId} · ${run.configurationId} · ${run.created}${run.createdBy ? ` · ${run.createdBy}` : ""}`;

// Searches earlier finished runs of any ESOI with a raw BOM in the environment,
// for Compare With = Previous Run. value is the Id of the chosen run.
export default function PreviousRunSelect({ id, environment, environments, value, onChange, invalid }) {
  const { instance, accounts } = useMsal();
  const [search, setSearch] = useState("");
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const containerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // The run must have a raw BOM in the environment, choose again when it changes
  useEffect(() => {
    setSelected(null);
    onChangeRef.current("");
  }, [environment]);

  useEffect(() => {
    if (!value) setSelected(null);
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    // Wait until the user stops typing
    const timer = setTimeout(async () => {
      setLoading(true);
      setFailed(false);
      try {
        const accessToken = await getToken(instance, accounts);
        if (!accessToken) return;
        const response = await axios.get(`${appConfig.apiUrl}inputs/previous-runs`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { search: search.trim(), environment, take: 20 },
        });
        if (cancelled) return;
        if (response.data?.status === "OK") {
          setRuns(response.data.data || []);
        } else {
          setFailed(true);
        }
      } catch (err) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, search, environment, instance, accounts]);

  // Close the list when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const choose = (run) => {
    setSelected(run);
    setOpen(false);
    setSearch("");
    onChange(String(run.inputId));
  };

  const clear = () => {
    setSelected(null);
    onChange("");
  };

  if (selected && value) {
    return (
      <div className="d-flex align-items-center gap-2">
        <div className="form-control text-truncate" title={`${runText(selected)}\n${selected.codeString}`}>
          {runText(selected)}
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clear}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="position-relative">
      <input
        id={id}
        type="text"
        className={`form-control${invalid ? " is-invalid" : ""}`}
        aria-invalid={invalid || undefined}
        placeholder="Search by ESOI or run #Id"
        value={search}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
      />
      {open && (
        <div
          className="list-group position-absolute w-100 shadow-sm text-start"
          style={{ zIndex: 20, maxHeight: "260px", overflowY: "auto" }}
        >
          {loading && <div className="list-group-item small text-muted">Searching...</div>}
          {!loading && failed && <div className="list-group-item small text-danger">The runs could not be loaded.</div>}
          {!loading && !failed && !runs.length && (
            <div className="list-group-item small text-muted">
              No finished runs in {environmentLabel(environment, environments)}{search.trim() ? ` match "${search.trim()}"` : ""}.
            </div>
          )}
          {!loading &&
            runs.map((run) => (
              <button
                key={run.inputId}
                type="button"
                className="list-group-item list-group-item-action small d-block"
                onClick={() => choose(run)}
                title={run.codeString}
              >
                <div className="fw-semibold">#{run.inputId} · {run.configurationId} · Format {run.formatId}</div>
                <div className="text-muted">
                  {run.created}{run.createdBy ? ` · ${run.createdBy}` : ""} · {run.uploadName}
                </div>
              </button>
            ))}
        </div>
      )}
      <div className="small text-muted mt-1">Pick the run whose {environmentLabel(environment, environments)} BOM is compared.</div>
    </div>
  );
}
